interface SkipLinkProps {
  /** ID of the main content element to skip to */
  targetId?: string;
  /** Text to display in the skip link */
  children?: React.ReactNode;
}

/**
 * Skip link component for keyboard users to bypass navigation.
 * Visible only when focused, positioned at the top of the page.
 *
 * Place this component as the first focusable element in the page.
 * Add id="main-content" to your main content area.
 *
 * @example
 * <SkipLink />
 * <Header />
 * <main id="main-content">...</main>
 */
export default function SkipLink({
  targetId = 'main-content',
  children = 'Skip to main content',
}: SkipLinkProps) {
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const target = document.getElementById(targetId);
    if (target) {
      // Set tabindex if not focusable
      if (!target.hasAttribute('tabindex')) {
        target.setAttribute('tabindex', '-1');
      }
      target.focus();
      // Scroll into view
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <a
      href={`#${targetId}`}
      onClick={handleClick}
      className="
        sr-only focus:not-sr-only
        focus:absolute focus:z-[9999] focus:top-4 focus:left-4 focus:m-0
        focus:px-4 focus:py-2
        focus:bg-primary-600 focus:text-white
        focus:rounded-lg focus:shadow-lg
        focus:outline-none focus:ring-2 focus:ring-primary-400 focus:ring-offset-2
        font-medium text-sm
        transition-all
      "
    >
      {children}
    </a>
  );
}
